// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## TimeSignature
//
// An object.
import type { TimeSignatureAlias } from 'shared/definitions/musicTheory/rhythms';
import {
  meterBeamDuration,
  meterBeatDuration,
  meterSubdivision,
  isTimeSignatureString,
} from 'shared/definitions/musicTheory/rhythms';
import { arrayify } from 'shared/src/utils/arrayify';
import Rhythm from './rhythm';
import Fraction from './fraction';
import formatTimeSignature from './formatTimeSignature';
import type { AriaStringOptions } from './note';

interface TimeSignatureOptions {
  /** @prop { boolean } shorten 2 / 2 becomes ¢, 4 / 4 becomes c */
  shorten?: boolean;
  /** @prop { string } displayAs "26/13" instead of 4 / 2, for instance... */
  displayAs?: string;
}

/**
 * A TimeSignature in music
 * @param {string} name (4/4, 2/2, etc... no shorthand here!)
 * @param {Object} options
 * @param {boolean} options.shorten 2/2 becomes ¢, 4/4 becomes c
 * @param {string} options.displayAs "26/13" instead of 4/2, for instance...
 */
class TimeSignature {
  /** @prop {number} upper upper number of time signature */
  upper = 4;

  /** @prop {number} lower upper number of time signature */
  lower = 4;

  /** @prop {Rhythm} beatDuration */
  beatDuration!: Rhythm;

  /** @prop {Rhythm[]} beatDurations for measures with mixed beat lengths, e.g., 5/8 */
  beatDurations!: Rhythm[];

  /** @prop {Rhythm} beamDuration */
  beamDuration!: Rhythm;

  /** Rhythms indicating the length of each beam, may vary in mixed meters */
  beamDurations!: Rhythm[];

  /** Fractions indicating where each beat begins */
  beatPointers!: Fraction[];

  /** Fractions indicating where each beaming group begins */
  beamPointers!: Fraction[];

  /** @prop {Fraction} measureFraction */
  measureFraction!: Fraction;

  /** @prop {number} beatsPerMeasure */
  beatsPerMeasure!: number;

  /** @prop {number} subdivisionsPerBeat */
  subdivisionsPerBeat!: number;

  /** @prop {Rhythm} subdivisionDuration */
  subdivisionDuration!: Rhythm;

  /** @prop {string} displayAs another time signature, including non-existant ones, eg '1/1' */
  displayAs?: string;

  options: TimeSignatureOptions;

  constructor(name?: TimeSignatureAlias | TimeSignature, options: TimeSignatureOptions = {}) {
    this.options = options;
    this.set(name);
    if (this.options.displayAs) this.displayAs = this.options.displayAs;
  }

  set(name?: TimeSignatureAlias | TimeSignature) {
    const {
      isAbbreviation,
      measureFraction,
      beatDurations,
      beatDuration,
      beamDuration,
      beamDurations,
      beatPointers,
      beamPointers,
      beatsPerMeasure,
      subdivisionDuration,
      subdivisionsPerBeat,
      upper,
      lower,
    } = parseTSString(name);

    this.upper = upper;
    this.lower = lower;
    if (isAbbreviation) this.options.shorten = true;

    this.measureFraction = measureFraction;
    this.beatDuration = beatDuration;
    this.beatDurations = beatDurations;
    this.beatPointers = beatPointers;

    this.beamDuration = beamDuration;
    this.beamDurations = beamDurations;
    this.beamPointers = beamPointers;

    this.beatsPerMeasure = beatsPerMeasure;
    this.subdivisionDuration = subdivisionDuration;
    this.subdivisionsPerBeat = subdivisionsPerBeat;
  }

  private _hidden: boolean = false;

  /** @prop {boolean} hidden whether time signature should be drawn on staff or not */
  get hidden() {
    return !!this._hidden;
  }

  set hidden(hide) {
    this._hidden = !!hide;
  }

  /** @prop {boolean} isSimple is it a simple meter? */
  get isSimple() {
    return !this.isMixed && this.beatDuration.getDots() === 0;
  }

  /** @prop { boolean } isCompound is it a compound meter ? */
  get isCompound() {
    return !this.isMixed && this.beatDuration.getDots() === 1;
  }

  get isMixed() {
    return (
      this.beatDurations.length > 1 &&
      !this.beatDurations.every((duration) => duration.equals(this.beatDuration))
    );
  }

  /** @prop {boolean} isCutTime is it cut time? */
  get isCutTime() {
    return this.isSimple && this.beatDuration.fraction.greaterThanEquals(1 / 2);
  }

  /**
   * Get the fractional portion of a measureFraction at which each beat begins.
   * For instance, for 4/4 time:
   * ```typescript
   *   const beatDurations = [ new Rhythm('4') ];
   *   const measureFraction = new Fraction(4, 4);
   *   calculateBeatFractions(beatDurations, measureFraction);
   *   // Returns:
   *  [
   *    Fraction { numerator: 0, denominator: 1 },
   *    Fraction { numerator: 1, denominator: 4 },
   *    Fraction { numerator: 2, denominator: 4 },
   *    Fraction { numerator: 3, denominator: 4 }
   *  ]
   * ```
   */
  static calculateBeatFractions(beatDurations: Rhythm[], measureFraction: Fraction) {
    const durationFractions = beatDurations.map((rhythm) => rhythm.getFraction());
    const beatFractions = [new Fraction(0, 1)];
    const sum = () => beatFractions.reduce((accum, fract) => accum.add(fract), new Fraction(0, 1));

    while (sum().lessThan(measureFraction)) {
      const fract = durationFractions.shift()!;
      // We repeat the fraction at the end of the array so that if we have to loop over the start
      // of the array again we can:
      beatFractions.push(fract);
      durationFractions.push(fract);
    }
    const pointer = new Fraction(0, 1);
    return beatFractions.map((fract) => pointer.add(fract).clone()).slice(0, -1);
  }

  /**
   * Display as a different time signature
   */
  setDisplayAs(displayAs: `${number}/${number}` | 'C' | 'C|') {
    this.displayAs = displayAs;
  }

  equals(that: TimeSignature | TimeSignatureAlias) {
    that = TimeSignature.fromString(that);
    return this.toString() === that.toString();
  }

  /**
   * Get the rhythm representing the length of the first beat of the meter.
   */
  getBeatDuration() {
    return this.beatDuration;
  }

  getName() {
    return this.toString();
  }

  getVexName(name: string = this.displayAs || this.toString()): string {
    if (this.options.shorten) {
      if (name === '4/4') return 'C';
      if (name === '2/2') return 'C|';
    }
    return name;
  }

  toCSSString() {
    return formatTimeSignature(this.getVexName());
  }

  /**
   * Format a time signature (or time signature-like string) as a time
   * signature. Use C and C| for common and cut time.
   *
   * @param timeSignature string of TimeSignature -- can be irrational, anything
   * with a / will be formatted.
   */
  static toCSSString(timeSignature: string | TimeSignature) {
    const timeSignatureString =
      timeSignature instanceof TimeSignature ? timeSignature.toString() : timeSignature;
    return formatTimeSignature(timeSignatureString);
  }

  static fromString(string: TimeSignatureAlias | TimeSignature) {
    if (string instanceof TimeSignature) return string;
    return new TimeSignature(string);
  }

  toAriaString(ariaOptions: AriaStringOptions): string {
    const { timeSignature = undefined } = ariaOptions;
    const isHidden = timeSignature === undefined ? this.hidden : !timeSignature;
    if (isHidden) return '';
    if (this.displayAs) return `${this.displayAs} time`;
    return `${this.upper}/${this.lower} time`;
  }

  toString(includeAbbreviation: boolean = false): TimeSignatureAlias {
    if (includeAbbreviation && this.options.shorten && this.upper === 4 && this.lower === 4)
      return 'C';
    if (includeAbbreviation && this.options.shorten && this.upper === 2 && this.lower === 2)
      return 'C|';
    return `${this.upper}/${this.lower}` as TimeSignatureAlias;
  }
}

export default TimeSignature;

function parseTSString(tsOrTSString: TimeSignatureAlias | TimeSignature = '4/4') {
  let name = tsOrTSString instanceof TimeSignature ? tsOrTSString.getName() : tsOrTSString;
  let isAbbreviation = false;

  // Special cases of "C" and "C|"
  if (name === 'C') {
    name = '4/4';
    isAbbreviation = true;
  }
  if (name === 'C|') {
    name = '2/2';
    isAbbreviation = true;
  }

  if (!isTimeSignatureString(name)) {
    throw new Error(
      `NaM: The time signature name must be in the format 2/4, 9/8, etc... not ${name}`
    );
  }

  const parts = name.split('/');
  const upper = Number(parts[0]);
  const lower = Number(parts[1]);

  const measureFraction = new Fraction(upper, lower);

  // calculate beat duration & beats per measure:
  const beatDurations = arrayify(meterBeatDuration[name]).map((rhythm) => new Rhythm(rhythm));
  const beatDuration = beatDurations[0]!;

  const beamDurations = arrayify(meterBeamDuration[name]).map((rhythm) => new Rhythm(rhythm));
  const beamDuration = beamDurations[0]!;

  const beatPointers = TimeSignature.calculateBeatFractions(beatDurations, measureFraction);

  const beamPointers = TimeSignature.calculateBeatFractions(beamDurations, measureFraction);

  const beatsPerMeasure = measureFraction.clone().divide(beatDuration.fraction).value();

  const subdivisionDuration = new Rhythm(meterSubdivision[name]);

  const subdivisionsPerBeat = beatDuration
    .getFraction()
    .clone()
    .divide(subdivisionDuration.getFraction())
    .value();

  return {
    name,
    isAbbreviation,
    measureFraction,
    beatDurations,
    beatDuration,
    beamDuration,
    beamDurations,
    beatPointers,
    beamPointers,
    beatsPerMeasure,
    subdivisionDuration,
    subdivisionsPerBeat,
    upper,
    lower,
  };
}
